import { ChangeDetectionStrategy, Component, computed, HostListener, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { TranslocoModule } from '@jsverse/transloco';
import { DatePipe } from '@angular/common';
import { TableLazyLoadEvent } from 'primeng/table';
import { GenericParentGridComponent } from '../../generic/generic-parent-grid.component';
import { CesTitleComponent } from '../../ui/ces-route-title/ces-route-title.component';
import { EventLogStore } from '../../../stores/event-log.store';
import { EventLog } from '../../../types/event-log.interface';
import { eventLogDefinition } from '../../../entity-definitions/event-log.definition';
import { LazyLoadConverter } from '../../../utils/lazy-load.converter';
import { EntityDefinition } from '../../../types/entity-definition.interface';
import { ActivatedRoute, Router } from '@angular/router';
import { PaginatedUrlSyncService, ParsedFilters } from '../../../services/paginated-url-sync.service';
import { HttpClient } from '@angular/common/http';
import { Subject } from 'rxjs';

interface CalculJob {
  id: number;
  action: string;
  status: string;
  measureId: number | null;
  campaignId: number | null;
  message: string | null;
  createdBy: string | null;
  retryCount: number;
  created: string;
  startedAt: string | null;
}

@Component({
  selector: 'app-event-log-list',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CesTitleComponent, GenericParentGridComponent, TranslocoModule, DatePipe],
  templateUrl: './event-log-list.component.html',
})
export class EventLogsListComponent implements OnInit, OnDestroy {
  readonly #destroy$ = new Subject<void>();
  #isInitialLoad = true;

  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly eventLogStore = inject(EventLogStore);
  private readonly paginatedUrlSync = inject(PaginatedUrlSyncService);
  private readonly http = inject(HttpClient);

  readonly entityDefinition: EntityDefinition<EventLog> = eventLogDefinition;
  readonly urlFilters = signal<ParsedFilters | undefined>(
    this.paginatedUrlSync.parseFiltersFromUrl(this.route.snapshot.queryParams, this.entityDefinition),
  );

  readonly previousAssetId = signal<number | null>(this.#extractAssetIdFromPreviousUrl());

  // Hidden jobs panel — Ctrl+Shift+J
  readonly showJobs = signal(false);
  readonly activeJobs = signal<CalculJob[]>([]);
  readonly recentJobs = signal<CalculJob[]>([]);
  readonly jobsLoading = signal(false);

  @HostListener('document:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (event.ctrlKey && event.shiftKey && event.key === 'J') {
      event.preventDefault();
      const visible = !this.showJobs();
      this.showJobs.set(visible);
      if (visible) this.loadJobs();
    }
  }

  loadJobs(): void {
    this.jobsLoading.set(true);
    const active$ = this.http.get<CalculJob[]>('/calcul-jobs?status=todo,ongoing&limit=50');
    const recent$ = this.http.get<CalculJob[]>('/calcul-jobs?status=completed,error&limit=10');

    active$.subscribe({
      next: data => this.activeJobs.set(data),
      error: () => this.activeJobs.set([]),
    });
    recent$.subscribe({
      next: data => {
        this.recentJobs.set(data);
        this.jobsLoading.set(false);
      },
      error: () => this.jobsLoading.set(false),
    });
  }

  eventLogDefinition = eventLogDefinition;
  readonly eventLogs = computed(() => {
    return this.eventLogStore.eventLogs();
  });

  dataLoadingCompleted: boolean = false;

  readonly totalRecords = computed(() => this.eventLogStore.paginationState().totalItems);
  readonly first = computed(() => {
    const page = this.eventLogStore.paginationState().currentPage;
    const limit = this.eventLogStore.paginationState().limit;
    return (page - 1) * limit;
  });
  readonly rows = computed(() => this.eventLogStore.paginationState().limit);

  ngOnInit(): void {
    const assetId = this.previousAssetId();
    this.#loadEntitiesFromUrl(assetId ? { assetId } : undefined);
  }

  #loadEntitiesFromUrl(filter?: Record<string, unknown>): void {
    let queryParams = this.route.snapshot.queryParams;
    if (filter) {
      queryParams = { ...queryParams, filters: JSON.stringify(filter) };
    }
    const tableEvent = this.paginatedUrlSync.buildTableEventFromUrl(queryParams, this.entityDefinition);
    this.urlFilters.set(tableEvent.filters);
    const params = LazyLoadConverter.fromTableEvent(tableEvent, ['name', 'description', 'event-logs']);
    void this.eventLogStore.loadPaginatedEntities(params);
  }

  async onLazyLoad(event: TableLazyLoadEvent): Promise<void> {
    if (this.#isInitialLoad) {
      this.#isInitialLoad = false;
      return;
    }

    const queryParams = this.paginatedUrlSync.buildQueryParamsFromEvent(event, this.entityDefinition);
    await this.paginatedUrlSync.navigateWithParams(this.router, this.route, queryParams);

    const params = LazyLoadConverter.fromTableEvent(event, ['name', 'description', 'event-logs']);
    await this.eventLogStore.loadPaginatedEntities(params);
  }

  #extractAssetIdFromPreviousUrl(): number | null {
    const prevUrl = this.router.getCurrentNavigation()?.previousNavigation?.finalUrl?.toString();
    const match = prevUrl?.match(/\/assets\/(\d+)/);
    return match ? Number(match[1]) : null;
  }

  ngOnDestroy(): void {
    this.#destroy$.next();
    this.#destroy$.complete();
  }
}
